import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import type { GameProgressResponse } from "@models/game";

export interface LastSessionSummaryLineProps {
  progress: GameProgressResponse | null | undefined;
}

const LastSessionSummaryLine: FC<LastSessionSummaryLineProps> = ({
  progress,
}) => {
  const { t } = useTranslation();

  const line = useMemo(() => {
    const trend = progress?.trend ?? [];
    if (trend.length === 0) {
      return t("games.cta.widgets.lastSessionNever");
    }

    const dates = trend.map((e) => e.date?.slice(0, 10)).filter(Boolean);
    const lastDateStr = dates.length ? dates.sort().pop()! : "";
    if (!lastDateStr) return t("games.cta.widgets.lastSessionNever");

    const last = new Date(lastDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    last.setHours(0, 0, 0, 0);
    const diffMs = today.getTime() - last.getTime();
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

    if (diffDays === 0) {
      return t("games.cta.widgets.lastSessionToday");
    }
    if (diffDays === 1) {
      return t("games.cta.widgets.lastSessionYesterday");
    }
    if (diffDays >= 2 && diffDays <= 6) {
      return t("games.cta.widgets.lastSessionDaysAgo", { count: diffDays });
    }
    return t("games.cta.widgets.lastSessionDaysAgo", { count: diffDays });
  }, [progress?.trend, t]);

  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        fontSize: 17,
        textAlign: "center",
      }}
    >
      {line}
    </Typography>
  );
};

export default LastSessionSummaryLine;
